import { useState, useEffect } from 'react';
import { Building2, Plus, Users, Database } from 'lucide-react';
import { adminApi } from '../services/adminApi';

interface Tenant {
  tenant_id: string;
  tenant_name: string;
  created_at: string;
  status: string;
  user_count?: number;
}

export default function TenantManagement() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      const response = await adminApi.listTenants();
      setTenants(response.tenants || response.data || []);
    } catch (error) {
      console.error('[TENANT MANAGEMENT] Failed to load tenants:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTenant = () => {
    setShowCreateModal(true);
  };

  const handleViewUsers = (tenant: Tenant) => {
    // Navigate to users page with tenant filter
    console.log('[TENANT] View users for:', tenant.tenant_id);
    alert(`View users for ${tenant.tenant_name} - Feature coming soon`);
  };

  const handleBackup = async (tenant: Tenant) => {
    try {
      console.log('[TENANT] Creating backup for:', tenant.tenant_id);
      await adminApi.backupTenant(tenant.tenant_id);
      alert(`Backup created successfully for ${tenant.tenant_name}`);
    } catch (error) {
      console.error('[TENANT] Backup failed:', error);
      alert('Failed to create backup');
    }
  };

  const handleSettings = (tenant: Tenant) => {
    console.log('[TENANT] Settings for:', tenant.tenant_id);
    alert(`Settings for ${tenant.tenant_name} - Feature coming soon`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-normal tracking-tight text-foreground mb-1">
            Tenant Management
          </h1>
          <p className="text-body-small text-muted-foreground font-normal tracking-tight">
            Manage enterprise tenant environments
          </p>
        </div>
        <button onClick={handleCreateTenant} className="px-4 py-1.5 bg-primary text-primary-foreground text-body-small font-normal tracking-tight rounded-md hover:bg-primary/90 transition-colors flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Create New Tenant
        </button>
      </div>

      {/* Tenants Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading ? (
          <p className="text-body-small text-muted-foreground">Loading tenants...</p>
        ) : tenants.length === 0 ? (
          <div className="col-span-full bg-card border border-border rounded-lg p-12 text-center">
            <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-body-small text-muted-foreground">No tenants found</p>
          </div>
        ) : (
          tenants.map((tenant) => (
            <div
              key={tenant.tenant_id}
              onClick={() => setSelectedTenant(tenant)}
              className="bg-card border border-border rounded-lg p-4 cursor-pointer hover:border-primary/20 hover:bg-accent/50 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-normal tracking-tight text-foreground mb-1 truncate">
                    {tenant.tenant_name}
                  </h3>
                  <p className="text-xs text-muted-foreground truncate">{tenant.tenant_id}</p>
                </div>
                <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0 ml-2" />
              </div>
              
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {tenant.user_count || 0} users
                </div>
                <div className="flex items-center gap-1">
                  <Database className="w-3 h-3" />
                  Active
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Created {new Date(tenant.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Tenant Details (when selected) */}
      {selectedTenant && (
        <div className="admin-card">
          <h2 className="text-lg font-normal tracking-tight text-gray-900 mb-6">
            Tenant Details: {selectedTenant.tenant_name}
          </h2>
          
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-xs text-gray-600 mb-1">Tenant ID</p>
              <p className="text-sm font-normal tracking-tight">{selectedTenant.tenant_id}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Status</p>
              <span className="text-xs px-2 py-1 bg-green-100 text-green-800">
                {selectedTenant.status}
              </span>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Created</p>
              <p className="text-sm font-normal tracking-tight">
                {new Date(selectedTenant.created_at).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Users</p>
              <p className="text-sm font-normal tracking-tight">{selectedTenant.user_count || 0}</p>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button 
              onClick={() => handleViewUsers(selectedTenant)}
              className="admin-button-secondary"
            >
              View Users
            </button>
            <button 
              onClick={() => handleBackup(selectedTenant)}
              className="admin-button-secondary"
            >
              Backup Data
            </button>
            <button 
              onClick={() => handleSettings(selectedTenant)}
              className="admin-button-secondary"
            >
              Settings
            </button>
          </div>
        </div>
      )}

      {/* Create Tenant Modal - TODO: Implement modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-normal tracking-tight text-foreground mb-4">
              Create New Tenant
            </h2>
            <p className="text-body-small text-muted-foreground mb-6">
              Feature coming soon. Tenant provisioning will be available in the next update.
            </p>
            <button 
              onClick={() => setShowCreateModal(false)}
              className="px-4 py-1.5 bg-primary text-primary-foreground text-body-small font-normal tracking-tight rounded-md hover:bg-primary/90 transition-colors w-full"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
